import { Suspense, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { OnboardingBanner } from './OnboardingBanner';
import { ErrorBoundary } from '../ErrorBoundary';
import { FullSpinner } from '../ui/Spinner';
import { useUIStore } from '../../stores/uiStore';

const titles: Record<string, string> = {
  '/': 'Dashboard',
  '/catalog': 'Catalog',
  '/ai-settings': 'AI Settings',
  '/conversations': 'Conversations',
  '/orders': 'Orders',
  '/payments': 'Payments',
  '/analytics': 'Analytics',
  '/settings': 'Settings',
};

function titleFor(pathname: string): string {
  if (titles[pathname]) return titles[pathname];
  if (pathname.startsWith('/catalog/new')) return 'New Product';
  if (pathname.startsWith('/catalog/import')) return 'Bulk Import';
  if (pathname.startsWith('/catalog/')) return 'Edit Product';
  const seg = pathname.split('/')[1];
  return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : 'Dashboard';
}

export function AppShell() {
  const location = useLocation();
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen);
  const setMobileOpen = useUIStore((s) => s.setMobileSidebarOpen);
  const title = useMemo(() => titleFor(location.pathname), [location.pathname]);
  const mobileDialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = mobileDialog.current;
    if (!mobileOpen || !dialog) return;
    const desktop = window.matchMedia('(min-width: 768px)');
    if (desktop.matches) {
      setMobileOpen(false);
      return;
    }
    // A modal dialog makes the background inert and contains keyboard focus.
    dialog.showModal();
    const onResize = () => { if (desktop.matches) setMobileOpen(false); };
    desktop.addEventListener('change', onResize);
    return () => {
      desktop.removeEventListener('change', onResize);
      if (dialog.open) dialog.close();
    };
  }, [mobileOpen, setMobileOpen]);

  return (
    <div className="flex h-dvh overflow-hidden bg-surface">
      <a
        href="#cartiq-main"
        className="sr-only z-[60] rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-white focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Skip to content
      </a>
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Mobile sidebar */}
      <dialog
        ref={mobileDialog}
        id="cartiq-mobile-navigation"
        aria-label="Navigation menu"
        className="fixed inset-y-0 left-0 right-auto m-0 h-dvh max-h-none w-[min(20rem,calc(100vw-1.5rem))] max-w-none overflow-hidden border-0 bg-sidebar p-0 text-primary backdrop:bg-primary/40 backdrop:backdrop-blur-sm md:hidden"
        onCancel={(event) => { event.preventDefault(); setMobileOpen(false); }}
        onClose={() => { if (!mobileDialog.current?.open) setMobileOpen(false); }}
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('a[href],button:not([disabled])')]
            .filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (!first || !last) return;
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right ||
              event.clientY < bounds.top || event.clientY > bounds.bottom) setMobileOpen(false);
        }}
      >
        {mobileOpen && <Sidebar mobile />}
      </dialog>

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar title={title} />
        <OnboardingBanner />
        <main id="cartiq-main" tabIndex={-1} className="min-w-0 flex-1 overflow-y-auto scrollbar-thin">
          <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-8">
            <ErrorBoundary key={location.pathname}>
              {/* Inner Suspense keeps the sidebar + top bar mounted while a
                  lazily-loaded page chunk is fetched, so only the content area
                  shows a loader instead of the whole shell flashing. */}
              <Suspense fallback={<FullSpinner />}>
                <div key={location.pathname} className="animate-fade-in-up">
                  <Outlet />
                </div>
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
